import { type Dispatch, memo, type SetStateAction, useEffect } from 'react';

import { useMotionValue } from 'motion/react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/shadcn/accordion';
import { Label } from '@/shadcn/label';
import { cn } from '@/shadcn/lib/utils';
import { RadioGroup, RadioGroupItem } from '@/shadcn/radio-group';

import { AvatarComponent } from '@/components/ui/avatar/avatar';
import type { NextAction } from '../actionList.types';

export interface PrioritySettings {
  priorityGroupLabel: string;
  priorityActionKey: string[];
}

export interface Link {
  groupKey: string;
  actionKey: string;
}

interface GroupActionsListProps {
  groupActions: NextAction;
  selectedLink: Partial<Link>;
  setSelectedLink: Dispatch<SetStateAction<Partial<Link>>>;
  priority?: PrioritySettings;
}

export const GroupActionsList = memo<GroupActionsListProps>(
  ({ groupActions, selectedLink, setSelectedLink, priority }) => {
    const test = useMotionValue('');

    useEffect(() => {
      test.set('only-item');
    }, []);

    // check if one of the actions is currently selected
    const selectedGroupAction =
      selectedLink?.groupKey === groupActions?.group?.label
        ? groupActions.actions.find(
            action => selectedLink.actionKey === action.key
          )
        : undefined;

    return (
      <Accordion
        collapsible
        data-cy="groupHeader"
        defaultValue="only-item"
        type="single"
      >
        <AccordionItem className="w-full py-2" value="only-item">
          <AccordionTrigger className="w-full cursor-pointer items-center px-2 py-2">
            <section className="flex w-full flex-row items-center justify-between gap-2">
              <div className="flex shrink flex-row items-center">
                <div className="mr-[15px]">
                  <AvatarComponent
                    name={groupActions.group.name}
                    size={35}
                    source={groupActions.group.avatar}
                  />
                </div>
                <div className="w-[300px] truncate font-semibold text-foreground/60 text-sm">
                  {`As ${groupActions.group.name}`}
                </div>
              </div>
              <div className="flex shrink-0 flex-row items-center justify-end text-nowrap text-foreground/60 text-xs">
                {`${groupActions.actions.length} action${
                  groupActions.actions.length > 1 ? 's' : ''
                }`}
              </div>
            </section>
          </AccordionTrigger>
          <AccordionContent className="ml-[55px] pb-2">
            <RadioGroup
              className="flex flex-col gap-0"
              onValueChange={value => {
                setSelectedLink({
                  groupKey: groupActions.group.label,
                  actionKey: value
                });
              }}
            >
              {groupActions.actions.map(action => {
                const uniqueKey = `${groupActions.group.label}-${action.key}`;
                const isPriority =
                  priority &&
                  groupActions.group.label === priority.priorityGroupLabel &&
                  priority.priorityActionKey.includes(action.key);
                return (
                  <div
                    className="flex h-7 cursor-pointer flex-row items-center gap-2 text-foreground/60 hover:text-foreground/90"
                    key={uniqueKey}
                  >
                    <RadioGroupItem
                      checked={selectedGroupAction?.key === action.key}
                      data-cy={`next-action-${uniqueKey}`}
                      id={uniqueKey}
                      value={action.key}
                    />
                    <Label
                      className={cn(
                        'flex h-full grow cursor-pointer items-center truncate pt-0.5',
                        isPriority ? 'font-bold' : 'font-medium'
                      )}
                      htmlFor={uniqueKey}
                    >
                      {action.title ?? action.key}
                    </Label>
                  </div>
                );
              })}
            </RadioGroup>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    );
  }
);
